import {CAS_FRONTEND_URL, DASHBOARD_TARGET} from "api/axios";
import accountantApi from "api/accountant";
import useChats from "contexts/Chat/useChats";
import useNotificationDots from "hooks/useNotificationDots";
import useUser from "contexts/User/useUser";
import {CalendarDays, ChevronLeft, LogOut, Megaphone, MessageCircle, Settings, Sparkles, User} from "lucide-react";
import {LucideIcon} from "lucide-react";
import {FC, useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router";
import {useQuery} from "@tanstack/react-query";

type CasTier = "free" | "education" | "course_access" | "pro";

const TIER_LABEL: Record<CasTier, string> = {
  pro: "CAS Academy Pro",
  education: "Education Member",
  course_access: "Course Access",
  free: "Free Account",
};

const CAS_ENABLED = DASHBOARD_TARGET === "cas";

function TierBadge({tier, isDemo, onUpgradeClick}: {tier: CasTier; isDemo?: boolean; onUpgradeClick?: () => void}) {
  return (
    <div className="flex items-center gap-2">
      {tier === "pro" ? (
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border bg-aka-amber-wash border-aka-amber-border text-aka-amber-dark">
          <Sparkles className="w-3 h-3" />
          CAS Academy Pro
        </span>
      ) : tier === "education" ? (
        <span className="hidden sm:inline text-xs font-medium px-2.5 py-1 rounded-full border bg-aka-amber-bg border-aka-border text-aka-amber-mid">
          Education Member
        </span>
      ) : (
        <span className="hidden sm:inline text-xs font-medium px-2.5 py-1 rounded-full border bg-aka-tag-bg border-aka-border text-aka-subtle">
          {isDemo ? "Demo" : tier === "course_access" ? "Course Access" : "Free Account"}
        </span>
      )}
      {tier === "free" && (
        <button
          onClick={onUpgradeClick}
          className="text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity bg-aka-progress text-white"
        >
          Become a Member →
        </button>
      )}
      {tier === "course_access" && (
        <button
          onClick={onUpgradeClick}
          className="text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity bg-aka-amber text-white"
        >
          Upgrade Now
        </button>
      )}
      {tier === "education" && (
        <button
          onClick={onUpgradeClick}
          className="text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity bg-aka-amber text-white"
        >
          Upgrade to Pro
        </button>
      )}
    </div>
  );
}

function initials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function roleLabel(type: string) {
  if (type === "recruiter") return "Recruiter Account";
  if (type === "admin") return "Admin Account";
  return "Account";
}

const EVENT_COLOR: Record<string, string> = {
  "Study Hall": "oklch(0.6 0.12 185)",
  "Monday Momentum": "oklch(0.65 0.2 45)",
  "Office Hours": "oklch(0.55 0.18 295)",
  "Free Class Friday": "oklch(0.75 0.18 90)",
  "Student-Led Study Hall": "oklch(0.6 0.15 160)",
  Webinar: "oklch(0.4593 0.0663 252.32)",
};

function eventColor(typeName: string) {
  if (EVENT_COLOR[typeName]) return EVENT_COLOR[typeName];
  for (const key of Object.keys(EVENT_COLOR)) {
    if (typeName.startsWith(key)) return EVENT_COLOR[key];
  }
  return "oklch(0.55 0.14 265)";
}

export interface RouteConfig {
  path: string;
  onClick?: () => void;
  name: string;
  title?: string;
  icon: LucideIcon;
  staticBack?: {label: string; url: string; onClick?: () => void};
}

const AppHeader: FC<{routes?: RouteConfig[]}> = ({routes = []}) => {
  const user = useUser();

  const {data: casMe} = useQuery({
    queryKey: ["cas-me", user.id],
    queryFn: () => accountantApi.getCasMe(),
    enabled: user.type === "accountant",
    staleTime: 1000 * 60 * 5,
  });

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime();
  const {data: monthEvents = []} = useQuery({
    queryKey: ["cas-calendar", now.getFullYear(), now.getMonth() + 1],
    queryFn: () => accountantApi.getCalendarEvents(monthStart, monthEnd),
    enabled: user.type === "accountant",
    staleTime: 1000 * 60 * 5,
  });
  const todayStr = now.toDateString();
  const todayEvents = monthEvents.filter(
    (e) => !e.locked && new Date(e.startUtc).toDateString() === todayStr && new Date(e.endUtc) > now,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const locationState = location.state as {backLabel?: string} | null;

  const isDashboardSubPage = ["/messages", "/settings", "/chat", "/proposals"].some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );

  const matchedRoute = routes.find((r) => pathname.startsWith(r.path));
  const isSubPage = !!(
    matchedRoute &&
    pathname !== matchedRoute.path &&
    pathname[matchedRoute.path.length] === "/" &&
    !pathname.startsWith("/chat")
  );
  const isUnlistedPage = !matchedRoute && !pathname.startsWith("/chat") && !isDashboardSubPage;
  const currentRoute = matchedRoute || (pathname.startsWith("/chat") ? {name: "BooksMatch Messages"} : null);
  const pageTitle =
    (currentRoute && "title" in currentRoute ? (currentRoute as RouteConfig).title : currentRoute?.name) ?? "";

  const showBack = isSubPage || isUnlistedPage || isDashboardSubPage || !!matchedRoute?.staticBack;
  const backLabel = isDashboardSubPage
    ? "Back to Dashboard"
    : locationState?.backLabel ||
      (isSubPage || isUnlistedPage ? "Back" : null) ||
      matchedRoute?.staticBack?.label ||
      "Back";
  const onBack = () => {
    if (isDashboardSubPage) {
      window.location.href = CAS_FRONTEND_URL;
    } else if (matchedRoute?.staticBack) {
      if (matchedRoute.staticBack.onClick) {
        matchedRoute.staticBack.onClick();
      } else {
        window.location.href = matchedRoute.staticBack.url;
      }
    } else {
      navigate(-1);
    }
  };
  const {threads} = useChats();
  const hasUnreadMessages = threads?.some((t) => !!t.unread);
  const {hasUnreadAnnouncements, hasUnseenCalendar, markCalendarSeen} = useNotificationDots(todayEvents.length > 0);
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const onLogout = () => {
    if (signingOut) return;
    setSigningOut(true);
    if (CAS_ENABLED) {
      const returnUrl = encodeURIComponent(`${window.location.origin}/auth/logout`);
      window.location.href = `${CAS_FRONTEND_URL}/auth/logout?redirect=${returnUrl}`;
    } else {
      window.location.href = `${window.location.origin}/auth/logout`;
    }
  };

  const menuItem = (icon: LucideIcon, label: string, onClick: () => void, muted?: boolean, showDot?: boolean) => {
    const Icon = icon;
    return (
      <button
        onClick={() => {
          setOpen(false);
          onClick();
        }}
        className="flex items-center gap-2.5 px-3 py-2 rounded-lg w-full text-left hover:bg-brand-cream transition-colors"
        style={{color: muted ? "#C0392B" : "#3D3530"}}
      >
        <div className="relative flex-shrink-0">
          <Icon className="w-4 h-4" style={{color: muted ? "#C0392B" : "#8490AA"}} />
          {showDot && <div className="absolute w-1.5 h-1.5 rounded-full bg-red-500 -top-0.5 -right-0.5" />}
        </div>
        <span className="text-xs font-medium">{label}</span>
      </button>
    );
  };

  return (
    <header
      className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b"
      style={{backgroundColor: "#FFFEF9", borderColor: "#E8E4DF"}}
    >
      <div className="flex items-center gap-2.5 grow">
        {showBack ? (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-muted hover:text-brand-navy transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {backLabel}
          </button>
        ) : pageTitle ? (
          <h1 className="text-lg font-bold text-brand-navy w-max">{pageTitle}</h1>
        ) : null}
        {showBack || pageTitle ? <div className="w-px h-5 bg-border-section" /> : null}
        <img src="/akadian-logo-no-tagline.png" alt="Akadian" className="h-7 w-auto flex-shrink-0" />
      </div>

      <div className="flex items-center gap-2.5" ref={ref}>
        {user.type !== "accountant" ? (
          <span
            className="hidden sm:inline text-xs font-medium px-2.5 py-1 rounded-full border"
            style={{backgroundColor: "#FEF0E3", borderColor: "#E8E4DF", color: "#F07340"}}
          >
            {roleLabel(user.type)}
          </span>
        ) : casMe ? (
          <TierBadge
            tier={casMe.tier}
            isDemo={casMe.isDemo}
            onUpgradeClick={() => {
              window.location.href = CAS_FRONTEND_URL;
            }}
          />
        ) : null}

        <div className="relative">
          <button
            onClick={() => {
              setOpen((v) => {
                if (!v && hasUnseenCalendar) markCalendarSeen();
                return !v;
              });
            }}
            className="flex items-center gap-1.5 rounded-full focus:outline-none hover:opacity-80 transition-opacity"
          >
            <div className="relative">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
                style={{backgroundColor: "#EDE9E3", color: "#282D4D"}}
              >
                {initials(user.name)}
              </div>
              {(hasUnreadMessages || hasUnreadAnnouncements || hasUnseenCalendar) && (
                <div className="absolute top-0 right-0 w-2 h-2 rounded-full bg-red-500" />
              )}
            </div>
          </button>

          {open && (
            <div
              className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl border shadow-lg z-50 py-1.5 px-1.5"
              style={{borderColor: "#E8E4DF"}}
            >
              <div className="px-3 py-2 mb-1">
                <p className="text-xs font-semibold text-brand-navy truncate">{user.name}</p>
                <p className="text-xs text-brand-muted truncate">{user.email}</p>
                {user.type === "accountant" && casMe && (
                  <p className="text-xs text-brand-muted truncate opacity-60">
                    {casMe.isDemo ? "Demo" : TIER_LABEL[casMe.tier]}
                  </p>
                )}
              </div>
              <div className="h-px bg-brand-border mx-1 mb-1" />

              {!pathname.startsWith("/messages") &&
                menuItem(Megaphone, "Announcements", () => navigate("/messages"), false, hasUnreadAnnouncements)}
              {!pathname.startsWith("/profile") && menuItem(User, "BooksMatch Profile", () => navigate("/profile"))}
              {!pathname.startsWith("/chat") &&
                menuItem(MessageCircle, "BooksMatch Messages", () => navigate("/chat"), false, hasUnreadMessages)}

              {user.type === "accountant" && (
                <>
                  {todayEvents.length > 0 && (
                    <>
                      <div className="h-px bg-brand-border mx-1 my-1" />
                      <div className="px-3 pt-1.5 pb-0.5 flex items-center gap-1.5">
                        <p
                          className="text-[10px] font-semibold uppercase tracking-wide"
                          style={{color: "oklch(0.62 0.2 25)"}}
                        >
                          Today
                        </p>
                        {hasUnseenCalendar && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                      </div>
                      {todayEvents.map((ev) => (
                        <button
                          key={ev.occurrenceId}
                          className="px-3 py-1 flex items-center gap-2 text-xs w-full cursor-pointer hover:bg-brand-page transition-colors rounded-md"
                          style={{color: "#282D4D"}}
                          onClick={() => {
                            setOpen(false);
                            if (ev.meetingLink) {
                              window.open(ev.meetingLink, "_blank", "noopener,noreferrer");
                            }
                          }}
                        >
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{backgroundColor: ev.typeColor ?? eventColor(ev.typeName)}}
                          />
                          <span className="flex-1 truncate text-left">{ev.title}</span>
                          <span className="shrink-0 opacity-70">
                            {new Date(ev.startUtc)
                              .toLocaleTimeString("en-US", {hour: "numeric", minute: "2-digit"})
                              .replace(":00", "")
                              .replace(" AM", "am")
                              .replace(" PM", "pm")}
                          </span>
                        </button>
                      ))}
                    </>
                  )}

                  {menuItem(CalendarDays, "View Full Calendar", () => {
                    setOpen(false);
                    window.location.href = `${CAS_FRONTEND_URL}/calendar`;
                  })}
                </>
              )}

              {user.type !== "admin" && !pathname.startsWith("/settings") && (
                <>
                  <div className="h-px bg-brand-border mx-1 my-1" />
                  {menuItem(Settings, "Account Settings", () => navigate("/settings"))}
                </>
              )}
              <div className="h-px bg-brand-border mx-1 my-1" />
              {menuItem(LogOut, "Sign Out", onLogout, true)}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
